import { FileUploadStore } from '@sib/files';
import { useTranslation } from '@sib/i18n';
import { FileUploadMode } from '@sib/types';
import {
  cn,
  Dialog,
  DialogContent,
  DialogTitle,
  FullScreenDropzoneView,
  VisuallyHidden,
  FileManagerConfig,
} from '@sib/ui';
import { useStorageConnectivityCheck } from '@sib/utils';
import { FileManagerContent } from '../FileManager';
import { NoStorageConnection } from '../NoStorageConnection';

interface FileDropDialogProps {
  currentMode: FileUploadMode | null;
  onClose: () => void;
  onModeChange: (mode: FileUploadMode) => void;
  fileUploadStore: FileUploadStore;
  config: FileManagerConfig;
}

export const FileDropDialog = ({
  currentMode,
  onClose,
  onModeChange,
  fileUploadStore,
  config,
}: FileDropDialogProps) => {
  const { t } = useTranslation();
  const { storageConnectionFailed } = useStorageConnectivityCheck();
  const { entityName, supportedFormats, maxFileSize } = config;

  return currentMode ? (
    <Dialog open onOpenChange={isOpen => !isOpen && onClose()}>
      <DialogContent
        className={cn('flex flex-col', {
          'h-[526px] w-[817px] max-w-full items-center justify-center rounded-lg bg-white p-6 text-center':
            currentMode === FileUploadMode.Dropzone,
          'h-[554px] w-[1337px] max-w-full gap-7 p-7 max-lg:h-full max-lg:rounded-none!':
            currentMode === FileUploadMode.FileManager,
          'h-[526px] w-[817px]': storageConnectionFailed,
        })}
      >
        {storageConnectionFailed ? (
          <NoStorageConnection
            title={t('components.storageConnection.title')}
            description={
              <div className="flex flex-col gap-3">
                <p>{t('components.storageConnection.description')}</p>
              </div>
            }
          />
        ) : (
          <>
            <VisuallyHidden.Root>
              <DialogTitle>{t('fileManager.drag')}</DialogTitle>
            </VisuallyHidden.Root>
            {currentMode === FileUploadMode.Dropzone && (
              <FullScreenDropzoneView
                onBrowseClick={() => onModeChange(FileUploadMode.FileManager)}
                supportedFormats={supportedFormats}
                maxFileSize={maxFileSize}
                entityName={entityName}
              />
            )}

            {currentMode === FileUploadMode.FileManager && (
              <FileManagerContent
                closeDialog={onClose}
                fileUploadStore={fileUploadStore}
                config={config}
              />
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  ) : null;
};
